import { useState, useMemo, useEffect, useCallback } from "react";

import { Form, Modal, Input, Select, Switch, message } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { Check } from "lucide-react";
import countryList from "react-select-country-list";

import { Client } from "../../bitmaker/schemas/Billing/Client";

import { BitmakerAPIClient } from "../../services/BitmakerApp";

import { MyFormItem } from "./MyFormItem";

export type ClientFormModalProps = {
  open: boolean;
  onClose: () => void;
  editingClient: Client | null;
  onClientSaved?: () => void;
};

export default function ClientFormModal({
  open,
  onClose,
  editingClient,
  onClientSaved,
}: ClientFormModalProps) {
  const [form] = Form.useForm();

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const countryOptions = useMemo(
    () =>
      countryList()
        .getData()
        .map((c) => ({
          label: c.label,
          value: c.label,
        })),
    [],
  );

  const createRedmineAccount = Form.useWatch("createRedmineAccount", form);
  const createRedmineProject = Form.useWatch("createRedmineProject", form);

  useEffect(() => {
    if (!open) return;
    if (editingClient) {
      form.setFieldsValue(editingClient);
    } else {
      form.resetFields();
      form.setFieldsValue({
        createRedmineAccount: false,
        createRedmineProject: false,
      });
    }
  }, [open, editingClient, form]);

  useEffect(() => {
    if (!createRedmineAccount) {
      form.setFieldsValue({ accountName: undefined });
    }
  }, [createRedmineAccount, form]);

  useEffect(() => {
    if (!createRedmineProject) {
      form.setFieldsValue({ redmineProjectName: undefined });
    }
  }, [createRedmineProject, form]);

  const handleCancelModal = useCallback(() => {
    setSubmitError(null);
    onClose();
  }, [onClose]);

  const handleSubmit = async (values: any) => {
    const firstName = values.firstName?.trim();
    const companyName = values.companyName?.trim();

    if (!firstName && !companyName) {
      setSubmitError("First Name or Company Name is required");
      return;
    }

    setSubmitError(null);
    setIsSubmitting(true);

    const isEditing = editingClient !== null;
    const url = isEditing
      ? `/billing/clients/${editingClient?.id}`
      : "/billing/clients";
    const method: "post" | "put" = isEditing ? "put" : "post";

    const payload = { ...values };

    if (!isEditing) {
      if (!payload.createRedmineAccount) {
        delete payload.accountName;
      }

      if (!payload.createRedmineProject) {
        delete payload.redmineProjectName;
      }
    }

    if (isEditing) {
      delete payload.createRedmineAccount;
      delete payload.createRedmineProject;
      delete payload.accountName;
      delete payload.redmineProjectName;
    }

    try {
      BitmakerAPIClient[method](url, payload, (response) => {
        if (response instanceof Error || response.status >= 400) {
          console.error(
            `Error ${isEditing ? "updating" : "creating"} client: `,
            response,
          );
          setSubmitError(
            `Error ${isEditing ? "updating" : "creating"
            } client. Please try again.`,
          );
          setIsSubmitting(false);
          return;
        }

        message.success(
          `Client ${isEditing ? "updated" : "created"} successfully!`,
        );

        onClientSaved?.();
        onClose();
        setIsSubmitting(false);
      });
    } catch (err) {
      console.error("Unexpected error during form submission:", err);
      setSubmitError("An unexpected system error occurred. Please try again.");
      setIsSubmitting(false);
    }
  };

  const modalTitle = editingClient ? "Edit Client" : "Add New Client";

  return (
    <Modal
      title={modalTitle}
      open={open}
      onCancel={handleCancelModal}
      afterClose={() => {
        form.resetFields();
      }}
      footer={
        <div className="flex justify-end gap-2">
          {/* Cancel Button */}
          <button
            type="button"
            onClick={handleCancelModal}
            className="btn btn-gray"
          >
            Cancel
          </button>

          {/* Save Button */}
          <button
            form="clientModalForm"
            type="submit"
            className="btn btn-green"
            disabled={isSubmitting}
          >
            {isSubmitting ? <LoadingOutlined /> : <Check size={16} />}
            <span>{isSubmitting ? "Saving..." : "Save"}</span>
          </button>
        </div>
      }
      width="50%"
      className="custom-modal client-modal"
    >
      {submitError && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
          {submitError}
        </div>
      )}
      <Form
        id="clientModalForm"
        form={form}
        layout="vertical"
        className="grid grid-cols-3 gap-6 mt-6"
        onFinish={handleSubmit}
      >
        <div className="col-span-2 space-y-6">
          {/* Personal Information */}
          <div className="space-y-2">
            <h3 className="form-section-title">Personal Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <MyFormItem label="First Name" name="firstName">
                <Input placeholder="John" />
              </MyFormItem>
              <MyFormItem label="Last Name" name="lastName">
                <Input placeholder="Doe" />
              </MyFormItem>
            </div>
            <MyFormItem label="Company Name" name="companyName">
              <Input placeholder="Acme Corporation" />
            </MyFormItem>
            <MyFormItem
              label="Corporate Name (Legal/Full Name)"
              name="corporateName"
            >
              <Input placeholder="ACME CORPORATION S.A.C." />
            </MyFormItem>
          </div>

          {/* Contact Information */}
          <div className="space-y-2">
            <h3 className="form-section-title">Contact Information</h3>
            <MyFormItem label="Email Address" name="emailAddress">
              <Input type="email" placeholder="john@company.com" />
            </MyFormItem>
            <MyFormItem label="Phone Number" name="phoneNumber">
              <Input placeholder="+1 234 567 890" />
            </MyFormItem>
          </div>

          {/* Address */}
          <div className="space-y-2">
            <h3 className="form-section-title">Address</h3>
            <MyFormItem label="Address Line" name="addressLine">
              <Input placeholder="Av. Example 123" />
            </MyFormItem>
            <div className="grid grid-cols-2 gap-4">
              <MyFormItem label="Town/City" name="townCity">
                <Input placeholder="Lima" />
              </MyFormItem>
              <MyFormItem label="State/Province" name="stateProvince">
                <Input placeholder="Miraflores" />
              </MyFormItem>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <MyFormItem label="Postal Code" name="postalCode">
                <Input placeholder="15074" />
              </MyFormItem>
              <MyFormItem label="Country" name="country">
                <Select
                  placeholder="Select Country"
                  options={countryOptions}
                  showSearch
                  allowClear
                />
              </MyFormItem>
            </div>
          </div>

          {/* Tax Information */}
          <div className="space-y-2">
            <h3 className="form-section-title">Tax Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <MyFormItem label="Tax Name" name="taxName">
                <Input placeholder="RUC" />
              </MyFormItem>
              <MyFormItem label="Tax Number" name="taxNumber">
                <Input placeholder="12345678901" />
              </MyFormItem>
            </div>
          </div>
        </div>

        {/* Client Settings */}
        <div className="flex flex-col p-4 rounded-xl border h-fit space-y-2">
          <h3 className="font-semibold text-lg mb-4">Client Settings</h3>
          <MyFormItem label="Currency" name="currency">
            <Select
              options={[
                { label: "USD", value: "USD" },
                { label: "PEN", value: "PEN" },
              ]}
              placeholder="Select Currency"
            />
          </MyFormItem>
          <MyFormItem label="Language" name="language">
            <Select
              options={[
                { label: "English", value: "en" },
                { label: "Spanish", value: "es" },
              ]}
              placeholder="Select Language"
            />
          </MyFormItem>
          {/* Only when creating a client */}
          {!editingClient && (
            <div className="pt-3 border-t space-y-3">
              <h4 className="font-medium text-sm text-gray-700">
                Redmine Integration
              </h4>

              {/* Redmine ACCOUNT */}
              <MyFormItem
                label="Create ACCOUNT in Redmine"
                name="createRedmineAccount"
                valuePropName="checked"
              >
                <Switch />
              </MyFormItem>

              {createRedmineAccount && (
                <MyFormItem
                  name="accountName"
                  label="ACCOUNT name"
                  rules={[
                    { required: true, message: "Account name is required" },
                  ]}
                >
                  <Input placeholder="ACME" />
                </MyFormItem>
              )}

              {/* Redmine Project */}
              <MyFormItem
                name="createRedmineProject"
                label="Create Project in Redmine"
                valuePropName="checked"
              >
                <Switch />
              </MyFormItem>

              {createRedmineProject && (
                <MyFormItem
                  name="redmineProjectName"
                  label="Project name"
                  rules={[
                    { required: true, message: "Project name is required" },
                  ]}
                >
                  <Input placeholder="ACME Website" />
                </MyFormItem>
              )}
            </div>
          )}
        </div>
      </Form>
    </Modal>
  );
}
